/**
 * Date Utilities
 *
 * These utilities handle the timezone off-by-one bug that occurs when:
 * 1. HTML date input returns a local date string (YYYY-MM-DD)
 * 2. Backend stores it as UTC (2026-01-16T00:00:00Z)
 * 3. Browser converts back to local time, potentially showing previous day
 *
 * SOLUTION: Always use UTC for date-only values (no time component)
 */

/**
 * Converts an ISO date string to YYYY-MM-DD format for HTML date inputs.
 * Uses UTC to avoid timezone shifts.
 *
 * @param isoString - ISO date string from API (e.g., "2026-01-16T00:00:00.000Z")
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateForInput(isoString: string): string {
  if (!isoString) return '';

  const date = new Date(isoString);
  // Use UTC methods to avoid local timezone conversion
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Converts an HTML date input value (YYYY-MM-DD) to an ISO string.
 * Stores at UTC noon (12:00:00) to prevent day boundary issues in any timezone.
 *
 * @param dateString - Date string from HTML input (e.g., "2026-01-16")
 * @returns ISO string safe for storage (e.g., "2026-01-16T12:00:00.000Z")
 */
export function dateInputToISOString(dateString: string): string {
  if (!dateString) return new Date().toISOString();

  // Parse the date parts manually to avoid timezone issues
  const parts = dateString.split('-').map(Number);
  const year = parts[0] ?? new Date().getFullYear();
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;

  // Create date at UTC noon to avoid any timezone crossing day boundaries
  // UTC noon means even UTC-12 (furthest behind) shows same day
  const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));

  return utcDate.toISOString();
}

/**
 * Formats an ISO date string for display in pt-BR format (DD/MM/YYYY).
 * Uses UTC to avoid timezone shifts.
 *
 * @param isoString - ISO date string from API
 * @returns Formatted date string (e.g., "16/01/2026")
 */
export function formatDateDisplay(isoString: string): string {
  if (!isoString) return '';

  const date = new Date(isoString);
  // Use UTC methods to get the actual stored date
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();

  return `${day}/${month}/${year}`;
}

/**
 * Formats an ISO date string for display with localized options.
 * Uses UTC timezone to prevent off-by-one errors.
 *
 * @param isoString - ISO date string from API
 * @param options - Intl.DateTimeFormat options
 * @returns Localized date string
 */
export function formatDateLocalized(
  isoString: string,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }
): string {
  if (!isoString) return '';

  const date = new Date(isoString);
  // Force UTC timezone to prevent local timezone conversion
  return date.toLocaleDateString('pt-BR', {
    ...options,
    timeZone: 'UTC',
  });
}

/**
 * Gets the start and end dates of a month in UTC.
 * Safe for filtering transactions without timezone issues.
 *
 * @param year - The year
 * @param month - The month (1-12)
 * @returns Object with startDate and endDate as ISO strings
 */
export function getMonthDateRange(year: number, month: number): { startDate: string; endDate: string } {
  // First day of month at UTC midnight
  const startDate = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0));

  // Last day of month at UTC 23:59:59.999
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const endDate = new Date(Date.UTC(year, month - 1, lastDay, 23, 59, 59, 999));

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
}

/**
 * Gets the current month's date range in UTC.
 *
 * @returns Object with startDate and endDate as ISO strings
 */
export function getCurrentMonthDateRange(): { startDate: string; endDate: string } {
  const now = new Date();
  return getMonthDateRange(now.getUTCFullYear(), now.getUTCMonth() + 1);
}

/**
 * Calculates relative date text (today, yesterday, X days ago).
 * Uses UTC to ensure consistent day calculations.
 *
 * @param isoString - ISO date string from API
 * @returns Relative date text or formatted date
 */
export function getRelativeDateText(isoString: string): string {
  if (!isoString) return '';

  const date = new Date(isoString);
  const now = new Date();

  // Compare UTC dates to avoid timezone issues
  const dateUtc = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const nowUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

  const diffDays = Math.floor((nowUtc - dateUtc) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return `${diffDays} dias atrás`;

  return formatDateDisplay(isoString);
}

/**
 * Parses an ISO string and returns UTC date components.
 * Useful for comparisons and calculations.
 *
 * @param isoString - ISO date string
 * @returns Object with year, month (1-12), day
 */
export function parseUTCDate(isoString: string): { year: number; month: number; day: number } {
  const date = new Date(isoString);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}
